import { apiFetch } from "assets/api";
import * as React from 'react'
import constants from './constants'
import { apiUrl } from './api'

const FavoritesContext = React.createContext(null)

async function fetchConversionMap(userIds) {
    const response = await apiFetch(
        apiUrl(`/convert?userIds=${encodeURIComponent(userIds.join(','))}`)
    )

    if (!response.ok) {
        throw new Error(
            `Failed to convert favorite IDs: ${response.status} ${response.statusText}`
        )
    }

    return response.json()
}

function getLocalStorageFavorites() {
    if (typeof window === 'undefined') {
        return []
    }

    try {
        const storedFavorites = window.localStorage.getItem(
            'players/favorites'
        )

        if (!storedFavorites) {
            return []
        }

        const parsedFavorites = JSON.parse(storedFavorites)

        if (!Array.isArray(parsedFavorites)) {
            return []
        }

        return parsedFavorites.filter(
            favorite =>
                favorite &&
                favorite.playerId !== undefined &&
                favorite.playerId !== null
        )
    } catch (error) {
        console.error(
            'Failed to read favorites from localStorage',
            error
        )
        return []
    }
}

function saveLocalStorageFavorites(favorites) {
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.localStorage.setItem(
            'players/favorites',
            JSON.stringify(favorites)
        )
    } catch (error) {
        console.error(
            'Failed to save favorites to localStorage',
            error
        )
    }
}

async function convertOldFavorites() {
    if (typeof window === 'undefined') {
        return null
    }

    const oldFavorites = window.localStorage.getItem('favorites')

    if (!oldFavorites) {
        return null
    }

    try {
        const parsedFavorites = JSON.parse(oldFavorites)

        if (!Array.isArray(parsedFavorites)) {
            return null
        }

        const userIds = parsedFavorites
            .map(favorite => favorite?.userId)
            .filter(
                userId =>
                    userId !== undefined &&
                    userId !== null
            )

        if (userIds.length === 0) {
            window.localStorage.removeItem('favorites')
            return []
        }

        const conversionMap = await fetchConversionMap(userIds)

        const convertedFavorites = parsedFavorites
            .map(({ userId, ...favorite }) => {
                const playerId = conversionMap[userId]

                if (
                    playerId === undefined ||
                    playerId === null
                ) {
                    return null
                }

                return {
                    ...favorite,
                    playerId,
                }
            })
            .filter(Boolean)

        saveLocalStorageFavorites(convertedFavorites)
        window.localStorage.removeItem('favorites')

        return convertedFavorites
    } catch (error) {
        console.error(
            'Failed to convert old favorites',
            error
        )
        return null
    }
}

export const FavoritesProvider = ({ children }) => {
    const [data, setData] = React.useState(
        getLocalStorageFavorites
    )

    React.useEffect(() => {
        let active = true

        async function migrateFavorites() {
            const convertedFavorites =
                await convertOldFavorites()

            if (
                !active ||
                convertedFavorites === null
            ) {
                return
            }

            setData(currentFavorites => {
                const mergedFavorites = [
                    ...currentFavorites,
                ]

                for (const favorite of convertedFavorites) {
                    const alreadyExists =
                        mergedFavorites.some(
                            existingFavorite =>
                                existingFavorite.playerId ===
                                favorite.playerId
                        )

                    if (!alreadyExists) {
                        mergedFavorites.push(favorite)
                    }
                }

                saveLocalStorageFavorites(
                    mergedFavorites
                )

                return mergedFavorites
            })
        }

        migrateFavorites()

        return () => {
            active = false
        }
    }, [])

    const value = React.useMemo(
        () => ({
            data,
            setData,
        }),
        [data]
    )

    return (
        <FavoritesContext.Provider value={value}>
            {children}
        </FavoritesContext.Provider>
    )
}

function useFavoritesContext() {
    const context = React.useContext(
        FavoritesContext
    )

    if (!context) {
        throw new Error(
            'Favorites hooks must be used inside a FavoritesProvider'
        )
    }

    return context
}

export function indexOf(favorites, playerId) {
    return favorites.findIndex(
        favorite => favorite.playerId === playerId
    )
}

export const useAddFavorite = () => {
    const { setData } = useFavoritesContext()

    return React.useCallback(
        player => {
            if (
                !player ||
                player.playerId === undefined ||
                player.playerId === null
            ) {
                return
            }

            setData(currentFavorites => {
                if (
                    indexOf(
                        currentFavorites,
                        player.playerId
                    ) !== -1
                ) {
                    return currentFavorites
                }

                const updatedFavorites = [
                    ...currentFavorites,
                    player,
                ]

                saveLocalStorageFavorites(
                    updatedFavorites
                )

                return updatedFavorites
            })
        },
        [setData]
    )
}

export const useRemoveFavorite = () => {
    const { setData } = useFavoritesContext()

    return React.useCallback(
        playerId => {
            setData(currentFavorites => {
                const updatedFavorites =
                    currentFavorites.filter(
                        favorite =>
                            favorite.playerId !==
                            playerId
                    )

                if (
                    updatedFavorites.length ===
                    currentFavorites.length
                ) {
                    return currentFavorites
                }

                saveLocalStorageFavorites(
                    updatedFavorites
                )

                return updatedFavorites
            })
        },
        [setData]
    )
}

export const useFavorites = () => {
    return useFavoritesContext().data
}